import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';

import { Form, AnnotatedStamp, AnnotatedColour, AnnotatedOptions } from '../models/form';

export class FormService extends BaseService<Form> {
  override model = Form;
  override modelName = 'form';
  override modelLabel = 'Form';

  async saveForm(
    form: Form,
    images: Record<string, File> = {},
  ): Promise<ApiResponse<Form>> {
    const formData = new FormData();
    formData.append('_request', JSON.stringify(form.toJSON()));
    formData.append('_method', form.isNewRecord ? 'POST' : 'PUT');

    for (const [key, file] of Object.entries(images)) {
      formData.append(key, file);
    }

    const url = form.isNewRecord
      ? this.modelBaseUrl
      : `${this.modelBaseUrl}/${form.objectID()}`;

    const request = new ApiRequest({
      method: 'POST',
      url,
      data: formData,
    });

    return await this.requestItem(request, Form);
  }

  async editItem(
    form: Form,
    data: Record<string, any>,
    image?: File,
  ): Promise<ApiResponse<Form>> {
    const formData = new FormData();
    formData.append('_request', JSON.stringify(data));

    if (image) {
      formData.append('image', image);
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${form.objectID()}/editItem`,
      data: formData,
    });

    return await this.requestItem(request, Form);
  }

  async addItem(
    form: Form,
    data: Record<string, any>,
    image?: File,
  ): Promise<ApiResponse<Form>> {
    const formData = new FormData();
    formData.append('_request', JSON.stringify(data));

    if (image) {
      formData.append('image', image);
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${form.objectID()}/addItem`,
      data: formData,
    });

    return await this.requestItem(request, Form);
  }

  async deleteItem(
    form: Form,
    item: { toJSON: () => Record<string, any> },
  ): Promise<ApiResponse<Form>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${form.objectID()}/deleteItem`,
      data: item.toJSON(),
    });

    return await this.requestItem(request, Form);
  }

  async duplicate(
    form: Form,
  ): Promise<ApiResponse<Form>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${form.objectID()}/duplicate`,
    });

    return await this.requestItem(request, Form);
  }

  async stamps(): Promise<ApiListResponse<AnnotatedStamp>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/stamps`,
    });

    return await this.requestList(request, AnnotatedStamp);
  }

  async colours(): Promise<ApiListResponse<AnnotatedColour>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/colours`,
    });

    return await this.requestList(request, AnnotatedColour);
  }

  async annotatedOptions(): Promise<ApiResponse<AnnotatedOptions>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/annotatedOptions`,
    });

    return await this.requestItem(request, AnnotatedOptions);
  }
}
